import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, waLink, CONTACT } from "../lib/site";
import { Reveal } from "../components/Reveal";

const KeywordBand = () => {
  const { t } = useLang();
  const items = t("marquee");
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="border-y border-white/10 bg-navy-deep py-10 md:py-14" data-testid="story-keyword-band">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 md:gap-x-12">
        {list.map((item, i) => (
          <Reveal key={i} variant="fade" delay={i * 0.06} className="flex items-center gap-8 md:gap-12">
            <span className="font-display italic text-2xl md:text-4xl text-cream/85 whitespace-nowrap">{item}</span>
            {i < list.length - 1 && <span className="text-gold/70 text-lg md:text-xl">✦</span>}
          </Reveal>
        ))}
      </div>
    </div>
  );
};

const Chapter = ({ c }) => (
  <Reveal>
    <article
      className="grid md:grid-cols-[150px_1fr] gap-5 md:gap-12 py-10 md:py-14 border-t border-white/10"
      data-testid={`story-chapter-${c.n}`}
    >
      <div className="flex md:flex-col items-baseline md:items-start gap-4 md:gap-3">
        <span className="font-display text-4xl md:text-6xl leading-none text-gold/80">{c.n}</span>
        <span className="font-body text-[11px] tracking-[0.28em] uppercase text-cream/45">{c.label}</span>
      </div>
      <div>
        <h3 className="font-display text-3xl md:text-4xl tracking-tight text-cream leading-tight">{c.title}</h3>
        <p className="mt-4 md:mt-5 font-body text-base md:text-lg text-cream/70 leading-relaxed max-w-2xl">{c.body}</p>
      </div>
    </article>
  </Reveal>
);

const Testimonials = () => {
  const { t } = useLang();
  const data = t("testimonials");
  const items = data && Array.isArray(data.items) ? data.items : [];
  return (
    <section className="max-w-[1300px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="story-testimonials">
      <div className="text-center mb-14 md:mb-16">
        <Reveal>
          <div className="flex items-center justify-center gap-4 mb-5">
            <span className="h-px w-10 bg-gold/50" />
            <span className="font-body text-xs tracking-[0.3em] uppercase text-cream/60">{data.kicker}</span>
            <span className="h-px w-10 bg-gold/50" />
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight">{data.title}</h2>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-3 gap-5 md:gap-6">
        {items.map((r, i) => (
          <Reveal key={i} delay={i * 0.1} className="border border-white/10 bg-navy-deep p-8 md:p-9 flex flex-col hover:border-gold/40 transition-colors" >
            <div className="flex text-gold mb-5" data-testid={`testimonial-stars-${i}`}>
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} size={16} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <p className="font-display text-xl md:text-2xl italic leading-snug text-cream/90 flex-1">“{r.quote}”</p>
            <div className="mt-7 flex items-center gap-3">
              <span className="w-10 h-10 grid place-items-center rounded-full bg-gold/15 text-gold font-body font-semibold">{r.name[0]}</span>
              <div>
                <div className="font-body text-sm text-cream" data-testid={`testimonial-name-${i}`}>{r.name}</div>
                <div className="font-body text-xs text-cream/40">Google</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="text-center mt-12 md:mt-14">
        <Reveal>
          <a
            href={CONTACT.googleReviews}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="testimonials-google-btn"
            className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-7 py-3 font-body text-sm text-cream hover:border-gold hover:text-gold transition-colors"
          >
            {data.cta} <ArrowUpRight size={16} />
          </a>
        </Reveal>
      </div>
    </section>
  );
};

const PageHeader = ({ kicker, title, lead, image }) => (
  <section className="relative pt-40 md:pt-52 pb-14 md:pb-20 overflow-hidden" data-testid="story-header">
    <div className="absolute inset-0 opacity-25">
      <img src={image} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/60 via-navy/80 to-navy" />
    </div>
    <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex items-center gap-4 mb-6">
        <span className="h-px w-12 bg-gold/60" />
        <span className="font-body text-xs tracking-[0.3em] uppercase text-gold">{kicker}</span>
      </motion.div>
      <h1 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-[20ch]">{title}</h1>
      <p className="mt-7 font-body text-lg md:text-xl text-cream/70 max-w-2xl leading-relaxed">{lead}</p>
      <div className="mt-8 flex items-center gap-4 font-body text-xs md:text-sm tracking-[0.28em] uppercase text-cream/60">
        <span>Italia</span>
        <span className="h-px w-12 bg-gold/60" />
        <span>Corfù</span>
      </div>
    </div>
  </section>
);

const Signature = () => {
  const { t } = useLang();
  const lines = t("story.signature");
  const list = Array.isArray(lines) ? lines : [];
  return (
    <section className="relative border-t border-white/10 py-24 md:py-32 text-center overflow-hidden" data-testid="story-signature">
      <div className="relative max-w-[900px] mx-auto px-5">
        <Reveal>
          <div className="text-gold leading-none" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "clamp(3.5rem, 10vw, 7rem)" }}>
            {list[0]}
          </div>
        </Reveal>
        <div className="mt-8 space-y-1.5">
          {list.slice(1).map((l, i) => (
            <Reveal key={i} variant="fade" delay={0.1 + i * 0.08}>
              <p className="font-display italic text-xl md:text-2xl text-cream/80">{l}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Story() {
  const { t } = useLang();
  const journey = t("story.journey");
  const chapters = Array.isArray(journey) ? journey : [];
  const first = chapters.slice(0, 4);
  const rest = chapters.slice(4);

  return (
    <main data-testid="story-page">
      <PageHeader kicker={t("story.kicker")} title={t("story.pageTitle")} lead={t("story.lead")} image={IMAGES.colazione} />

      {/* First chapters */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-4 pb-8 md:pb-12">
        {first.map((c) => (
          <Chapter key={c.n} c={c} />
        ))}
      </section>

      {/* Image band with welcome quote */}
      <section className="relative h-[52vh] min-h-[380px] overflow-hidden" data-testid="story-quote-band">
        <img src={IMAGES.corfuSea} alt="Corfù" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy-deep/65" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <Reveal>
            <p className="font-display text-3xl md:text-5xl italic text-cream max-w-4xl leading-tight">
              “{t("story.welcome")}”
            </p>
          </Reveal>
        </div>
      </section>

      {/* Remaining chapters */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-10 pt-8 md:pt-12 pb-10 md:pb-16">
        {rest.map((c) => (
          <Chapter key={c.n} c={c} />
        ))}
      </section>

      <KeywordBand />

      <Testimonials />

      <Signature />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-32 text-center">
        <Reveal>
          <Link to="/menu" data-testid="story-menu-btn" className="inline-flex items-center gap-2 rounded-full bg-cream px-8 py-4 text-navy-deep font-body font-semibold tracking-wide transition-transform hover:scale-[1.03]">
            {t("common.discoverMenu")} <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
